'use client';

import { useState } from 'react';

/**
 * Admin "impersonate user" action. POSTs to `/auth/impersonate`, which verifies
 * the caller's admin status at the auth service and mints a REAL provider
 * session (access + refresh) for the target — set as cookies on the response —
 * then reloads into the app as that user. Works in-place even though the admin
 * is already logged in (no provider account-switch).
 *
 * Pass `onError` to surface failures (e.g. a toast); the hook itself only owns
 * the loading state and the redirect.
 */
export function useImpersonate(locale: string, onError?: (message: string) => void) {
  const [isImpersonating, setIsImpersonating] = useState(false);

  const impersonate = async (userId: string, redirectTo: string = `/${locale}`) => {
    setIsImpersonating(true);
    try {
      const res = await fetch('/auth/impersonate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetUserId: userId, redirectTo })
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        onError?.(err.error || 'Failed to impersonate user');
        setIsImpersonating(false);
        return;
      }
      const data = await res.json();
      window.location.href = data.redirectTo || `/${locale}`;
    } catch (error) {
      onError?.(error instanceof Error ? error.message : 'Unknown error');
      setIsImpersonating(false);
    }
  };

  return { impersonate, isImpersonating };
}
